// Hook to manage Pusher connection and subscriptions for workspace updates
import { useEffect, useState, useRef, useMemo } from 'react'
import Pusher, { Channel } from 'pusher-js'
// Import ProcessStatusType from the API package
import { useNavigationStore, type NavigationStoreState } from '../stores/navigation-store'
import type { ProcessStatusType } from '@vizible/api' // Updated import
import type { ResearchProjectMetadata } from '@vizible/db' // Import from db package
import { Logger } from '@vizible/utils/logger' // Added logger

// Enhanced event data interface to support granular progress
interface StatusUpdateData {
    researchId: string
    status?: ProcessStatusType // Now optional since we might just send step updates
    step?: string // Task step like 'FETCH_DATA_END', 'LANGUAGE_SIMILARITY', etc.
    metadata?: Partial<ResearchProjectMetadata> // Granular status updates
    lastProcessedAt?: string // Add optional timestamp
}

// No longer needs props
// interface UsePusherNotificationsProps {
//     workspaceId?: string
// }

const logger = new Logger('usePusherNotifications')

// Environment variables
const PUSHER_KEY = process.env.NEXT_PUBLIC_PUSHER_KEY
const PUSHER_CLUSTER = process.env.NEXT_PUBLIC_PUSHER_CLUSTER

export function usePusherNotifications() {
    // Removed props
    const [pusherClient, setPusherClient] = useState<Pusher | null>(null)
    // Use a Map to store multiple channel subscriptions: key = channelName, value = Channel
    const channelRefs = useRef<Map<string, Channel>>(new Map())

    // Get workspaces and the setter function from the store
    const workspaces = useNavigationStore((state: NavigationStoreState) => state.workspaces)
    const setWorkspaces = useNavigationStore((state: NavigationStoreState) => state.setWorkspaces)

    // Memoize the workspace IDs to prevent unnecessary effect runs if the workspace objects change but IDs don't
    const workspaceIds = useMemo(() => workspaces.map(ws => ws.id), [workspaces])

    useEffect(() => {
        console.log('🔍 Pusher env check:', {
            PUSHER_KEY,
            PUSHER_CLUSTER,
            hasKey: !!PUSHER_KEY,
            hasCluster: !!PUSHER_CLUSTER
        })

        if (!PUSHER_KEY || !PUSHER_CLUSTER) {
            logger.info('Pusher disabled: Missing config keys.')
            // Cleanup existing subscriptions if pusher is disabled mid-run
            if (pusherClient) {
                channelRefs.current.forEach((channel, channelName) => {
                    logger.debug(`Unsubscribing from ${channelName} due to disabled pusher`)
                    pusherClient.unsubscribe(channelName)
                })
                channelRefs.current.clear()
                // Optionally disconnect client: pusherClient.disconnect()
            }
            return
        }

        // Initialize Pusher client
        let client = pusherClient
        if (!client || client.connection.state === 'disconnected') {
            logger.info('Initializing new Pusher client instance...')
            client = new Pusher(PUSHER_KEY, {
                cluster: PUSHER_CLUSTER,
            })
            setPusherClient(client)

            client.connection.bind('error', (err: any) => {
                logger.error('Pusher connection error:', err)
            })
            client.connection.bind('connected', () => {
                logger.info('Pusher connected successfully.')
                // Re-subscribe to channels if connection was dropped and re-established
                // This logic might need refinement based on Pusher's auto-resubscribe behavior
            })
            client.connection.bind('disconnected', () => {
                logger.info('Pusher disconnected.')
            })
        }

        if (!client) {
            logger.error('Pusher client is unexpectedly null after initialization check.')
            return
        }

        // --- Subscribe/Unsubscribe Logic --- //
        const currentSubscriptions = channelRefs.current
        const desiredChannelNames = new Set(workspaceIds.map(id => `workspace-updates-${id}`))
        const currentChannelNames = new Set(currentSubscriptions.keys())

        // Unsubscribe from channels no longer needed
        currentChannelNames.forEach(channelName => {
            if (!desiredChannelNames.has(channelName)) {
                logger.debug(`Unsubscribing from Pusher channel: ${channelName}`)
                client!.unsubscribe(channelName)
                currentSubscriptions.delete(channelName)
            }
        })

        // Subscribe to new channels
        desiredChannelNames.forEach(channelName => {
            if (!currentChannelNames.has(channelName)) {
                logger.debug(`Subscribing to Pusher channel: ${channelName}`)
                const newChannel = client!.subscribe(channelName)

                newChannel.bind('pusher:subscription_succeeded', () => {
                    logger.debug(`Successfully subscribed to ${channelName}`)
                })
                newChannel.bind('pusher:subscription_error', (status: any) => {
                    logger.error(`Failed to subscribe to ${channelName}:`, status)
                })

                // Bind keystone status update event handler (using existing service event name)
                newChannel.bind('keystone-processing-update', (data: {
                    keystoneId: string
                    researchId: string
                    status: 'idle' | 'processing' | 'error'
                    lastProcessedAt?: string | null
                    timestamp?: string
                    error?: string
                }) => {
                    logger.debug(`🔔 Received keystone-processing-update event on ${channelName}:`, {
                        keystoneId: data.keystoneId,
                        status: data.status,
                        researchId: data.researchId,
                        lastProcessedAt: data.lastProcessedAt,
                        timestamp: data.timestamp
                    })
                    if (data.keystoneId) {
                        const setKeystoneProcessingStatus = useNavigationStore.getState().setKeystoneProcessingStatus
                        logger.debug(`🔄 Updating keystone processing status in zustand:`, {
                            keystoneId: data.keystoneId,
                            status: data.status,
                            timestamp: data.lastProcessedAt || data.timestamp
                        })
                        setKeystoneProcessingStatus(
                            data.keystoneId,
                            data.status,
                            data.lastProcessedAt || data.timestamp
                        )
                    } else {
                        logger.warn(`⚠️ Received keystone-processing-update without keystoneId:`, data)
                    }
                })

                // Bind the status update event handler
                newChannel.bind('research-project-processing-status-update', (data: StatusUpdateData) => {
                    logger.debug(`Received status-update event on ${channelName}:`, data)
                    if (data.researchId && (data.status || data.step || data.metadata)) {
                        // Get the workspaceId from the channel name
                        const workspaceId = channelName.replace('workspace-updates-', '')

                        // Get current state and update immutably
                        const currentWorkspaces = useNavigationStore.getState().workspaces
                        const updatedWorkspaces = currentWorkspaces.map(ws => {
                            if (ws.id === workspaceId) {
                                const updatedSearches = ws.searches.map(search => {
                                    if (search.id === data.researchId) {
                                        const updateMessage = data.status ? `status to ${data.status}` : `step ${data.step} progress`
                                        logger.info(`Updating ${updateMessage} for search ${data.researchId}`)

                                        // Convert metadata to dataStatus when process completes
                                        let updatedDataStatus = search.dataStatus
                                        if (data.status === 'complete' && data.metadata) {
                                            const metadata = data.metadata as any
                                            updatedDataStatus = {
                                                contentStatus: {
                                                    status: metadata.contentStatus || 'No Content',
                                                    contentCount: metadata.totalNumberOfContents || 0,
                                                    lastContentAt: metadata.lastContentDate ? new Date(metadata.lastContentDate) : null,
                                                    firstContentAt: metadata.firstContentDate ? new Date(metadata.firstContentDate) : null,
                                                    updatedAt: metadata.lastContentFetchedAt ? new Date(metadata.lastContentFetchedAt) : null,
                                                },
                                                metricsStatus: {
                                                    status: metadata.metricsStatus || 'Not Calculated',
                                                    lastCalculatedAt: metadata.lastCalculatedAt ? new Date(metadata.lastCalculatedAt) : null,
                                                    updatedAt: metadata.lastCalculatedAt ? new Date(metadata.lastCalculatedAt) : null,
                                                },
                                                playersStatus: {
                                                    status: metadata.playersStatus || 'No Players',
                                                },
                                            }
                                        }

                                        return {
                                            ...search,
                                            // Update main status if provided
                                            ...(data.status && { processStatus: data.status }),
                                            // Store current step if provided
                                            ...(data.step && { currentStep: data.step }),
                                            // Merge metadata updates if provided
                                            ...(data.metadata && { stepMetadata: data.metadata }),
                                            // Update dataStatus with fresh data when process completes
                                            ...(updatedDataStatus && { dataStatus: updatedDataStatus }),
                                            // Update lastProcessedAt only if provided and status is 'complete'
                                            lastProcessedAt:
                                                data.status === 'complete' && data.lastProcessedAt
                                                    ? new Date(data.lastProcessedAt)
                                                    : search.lastProcessedAt,
                                        }
                                    }
                                    return search
                                })
                                return { ...ws, searches: updatedSearches }
                            }
                            return ws
                        })

                        // Update the store with the new workspaces array
                        const updateType = data.status ? `status ${data.status}` : `step ${data.step}`
                        console.log(`[PUSHER DEBUG] About to update store for research ${data.researchId} with ${updateType}`)
                        setWorkspaces(updatedWorkspaces)
                        console.log(
                            `[PUSHER DEBUG] Store updated. Updated search count:`,
                            updatedWorkspaces.flatMap(ws => ws.searches).length
                        )

                        // Clear dashboard run details when task completes (idle, complete, error)
                        if (data.status === 'idle' || data.status === 'complete' || data.status === 'error') {
                            const clearDashboardRunDetails = useNavigationStore.getState().clearDashboardRunDetails
                            const setIsDashboardUIBusy = useNavigationStore.getState().setIsDashboardUIBusy
                            const triggerDataRefresh = useNavigationStore.getState().triggerDataRefresh

                            logger.info(`Task completed with status ${data.status}, clearing dashboard run details...`)
                            clearDashboardRunDetails()
                            setIsDashboardUIBusy(false)

                            // Trigger data refresh for the completed research
                            // This signals dashboard/newsfeed pages to refetch their data
                            if (data.status === 'complete' || data.status === 'idle') {
                                logger.info(`Triggering data refresh for research ${data.researchId}`)
                                triggerDataRefresh(data.researchId)
                            }
                        }

                        // Status update complete - let components refetch data naturally based on status changes
                    } else {
                        logger.warn('Received incomplete status-update event:', data)
                    }
                })
                currentSubscriptions.set(channelName, newChannel)
            }
        })

        // Update the ref
        channelRefs.current = currentSubscriptions

        // --- Cleanup --- //
        // This cleanup runs when the component unmounts
        // If we need cleanup when workspaceIds change, it happens above (unsubscribe logic)
        return () => {
            logger.debug('Running Pusher cleanup on unmount')
            // Disconnect is usually better handled globally or on logout
            // If called here, it disconnects on every minor parent re-render potentially
            // Consider if this cleanup is needed here or should be more global.

            // For now, let's just ensure no lingering subscriptions from this hook instance
            // This might be redundant if the component truly unmounts and the client is cleaned up elsewhere.
            // if (client) {
            //     channelRefs.current.forEach((_, channelName) => {
            //         client.unsubscribe(channelName)
            //     })
            // }
        }
        // Dependencies: setWorkspaces (utils now called inside effect)
    }, [pusherClient, workspaceIds, setWorkspaces])
}
